/**
 * `ACAD_TABLE` entity → `RenderableEntity` adapter (TYPED).
 *
 * # What is drawn, and why it is the real thing
 *
 * `ACAD_TABLE` is serialized as a block reference (`100 AcDbBlockReference`,
 * group 2) to an ANONYMOUS `*T<N>` block, plus the `AcDbTable` content model.
 * That `*T<N>` block holds the table's FULLY RENDERED graphics — the MTEXT of
 * every cell and the LINE grid — the same representation trick a dynamic
 * block uses with its `*U<N>` block, and it is what AutoCAD itself displays.
 *
 * So this adapter draws that block's content, flattened at the table's
 * insertion point and oriented by its horizontal-direction vector. That is the
 * faithful render, not a placeholder: the cells' text and rules come from the
 * drawing's own geometry, character for character.
 *
 * # Boundary (deliberate, documented)
 *
 * IMPLEMENTED: render, bounds, spatial index, picking, selection, layer
 * visibility, properties and round-trip.
 *
 * NOT IMPLEMENTED: editing cell values, cell formulas, cell styles / merges,
 * TABLESTYLE management and table breaking. Those are a separate feature (see
 * `tableEntitySchema`'s own boundary doc). Nothing here should be read as a
 * claim that a table is editable.
 *
 * # Shape
 *
 * One table produces MANY child renderables, so the result is the pre-existing
 * `block-insert` composite geometry: a single wrapper renderable (the one
 * spatial-index / pickable entity) carrying its flattened children as
 * render-only satellites. That is the identical mechanism `scene/
 * liftBlockInsert.ts` builds for an INSERT and `collectRenderSatellites`
 * already consumes — keyed on `geometry.kind`, not on the entity type — so no
 * renderer change is needed for tables.
 */

import {
  tableRotationDegrees,
  type TableEntity,
} from "@dxf-ai-editor/drawing-model";

import { applyOffset } from "../../coordinateOrigin";
import type { RenderableEntity } from "../../types";

import {
  boundsFromPoints,
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { blockReferenceChildContext } from "./blockReferenceContext";
import type { BlockContentFlattener } from "./blockReferenceFlatten";

/**
 * Typed adapter for the `Table` entity (`tableEntitySchema`).
 *
 * `flatten` is INJECTED rather than imported for the same reason
 * `multileaderFromEntity` takes it: importing `blockFromEntity` here would
 * close an import cycle through `typed/index.ts`'s dispatcher, which
 * `blockFromEntity` itself imports. `typedFirstRenderable.ts` passes the
 * production `flattenBlockReference`.
 */
export function tableFromEntity(
  entity: TableEntity,
  ctx: AdapterContext,
  flatten: BlockContentFlattener,
): RenderableEntity | null {
  if (entity.blockName === "") return null;
  const blocks = ctx.blocks ?? {};
  const insertion = {
    x: entity.insertionPoint.x,
    y: entity.insertionPoint.y,
    z: entity.insertionPoint.z ?? 0,
  };
  if (!Number.isFinite(insertion.x + insertion.y + insertion.z)) return null;

  // @codex PR #785 round 2 F2 — the table IS a block REFERENCE, so its `*T<N>`
  // graphics resolve their BYBLOCK colour / linetype / group-48 scale from the
  // TABLE, exactly as an INSERT's children resolve them from the INSERT. The
  // ambient `ctx` would instead resolve them from the layer (or from an
  // enclosing INSERT), so a table with an explicit colour drew ByBlock grid
  // lines and cell text in the wrong one. ONE builder with the INSERT path —
  // {@link blockReferenceChildContext} (`flattenBlockReference` itself passes
  // `ctx` verbatim by contract; threading is the caller's job).
  const contentCtx = blockReferenceChildContext(
    {
      color: entity.color,
      // The table's EFFECTIVE layer — the same value threaded as
      // `parentLayer` below, so ByLayer and layer-"0" inheritance agree.
      layer: entity.layer ?? "0",
      linetype: entity.linetype,
      linetypeScale: entity.linetypeScale,
      spaceId: entity.spaceId,
    },
    ctx,
  );

  const flattened = flatten({
    blockName: entity.blockName,
    // RAW (un-offset) insertion — the child adapters apply the single
    // `applyOffset` themselves, exactly as a real INSERT's flatten does.
    insertion,
    // AcDbTable carries NO scale factors: the `*T<N>` block's graphics are
    // authored at the table's real size, so the reference is unit-scaled.
    scale: { x: 1, y: 1, z: 1 },
    rotationDeg: tableRotationDegrees(entity),
    spaceId: entity.spaceId,
    parentEntityId: entity.id,
    blocks,
    // The BYBLOCK thread (above) — NOT the ambient `ctx`. The wrapper
    // renderable below still resolves its OWN appearance in the ambient one.
    ctx: contentCtx,
    // The table's own layer is the effective enclosing layer, so a `*T<N>`
    // child authored on layer "0" inherits it — the same rule the INSERT
    // flatten applies (`resolveBlockChildLayer`).
    parentLayer: entity.layer ?? "0",
  });
  if (flattened.length === 0) return null;

  const children: RenderableEntity[] = flattened.map((child) => ({
    ...child,
    renderParentId: entity.id,
  }));

  const corners: { x: number; y: number; z: number }[] = [];
  for (const child of children) {
    const b = child.bounds;
    if (!Number.isFinite(b.minX + b.minY + b.maxX + b.maxY)) continue;
    corners.push({ x: b.minX, y: b.minY, z: 0 });
    corners.push({ x: b.maxX, y: b.maxY, z: 0 });
  }
  const offsetInsertion = applyOffset(insertion);
  const bounds =
    corners.length > 0
      ? boundsFromPoints(corners)
      : {
          minX: offsetInsertion.x,
          minY: offsetInsertion.y,
          maxX: offsetInsertion.x,
          maxY: offsetInsertion.y,
        };

  return buildRenderableShell(
    entity,
    ctx,
    {
      kind: "block-insert",
      blockName: entity.blockName,
      insertion: offsetInsertion,
      scale: { x: 1, y: 1, z: 1 },
      rotation: tableRotationDegrees(entity),
      childRenderables: children,
    },
    bounds,
  );
}
